import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { storage } from "@/lib/storage";
import { formatMoney } from "@/lib/currencies";
import { openRazorpayCheckout, razorpayApi } from "@/lib/razorpay";
import { toast } from "sonner";
import { Wallet, Plus, Check } from "lucide-react";

const QUICK_AMOUNTS = [100, 500, 1000, 2000, 5000];

export default function WalletTopUp() {
  const navigate = useNavigate();
  const profile = storage.getProfile() || { id: "M000000", name: "Merchant" };
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const refreshWallet = async () => {
    try {
      const { data } = await razorpayApi.wallet(profile.id);
      setBalance(data.balance || 0);
    } catch (e) {}
  };

  useEffect(() => {
    refreshWallet();
    // eslint-disable-next-line
  }, []);

  const topUp = async () => {
    const amt = Number(amount);
    if (!amt) return;
    setBusy(true);
    try {
      await openRazorpayCheckout({
        amount: amt,
        currency: "INR",
        purpose: "wallet_topup",
        merchantId: profile.id,
        merchantName: profile.name,
        note: "Wallet top-up",
      });
      toast.success(`₹${amt} added to wallet`);
      setDone(true);
      await refreshWallet();
      setTimeout(() => {
        setDone(false);
        setAmount("");
      }, 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-shell" data-testid="wallet-topup-page">
      <Header title="Wallet Top-Up" subtitle="Add funds online · Spend offline" />

      <div className="card-glass gold-glow rounded-2xl p-6 mb-6 fade-slide-up relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[rgba(230,180,85,0.08)] blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={16} className="text-[#E6B455]" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#F7E6B4]/60 font-medium">
              Current wallet balance
            </span>
          </div>
          <div className="text-5xl font-bold gold-text" data-testid="wallet-balance">
            {formatMoney(balance, "INR")}
          </div>
          <div className="text-xs text-[#F7E6B4]/55 mt-2">Available for offline spending</div>
        </div>
      </div>

      <h2 className="text-base font-semibold text-[#F7E6B4] mb-3">Quick top-up</h2>
      <div className="grid grid-cols-5 gap-2 mb-5">
        {QUICK_AMOUNTS.map((v) => (
          <button
            key={v}
            onClick={() => setAmount(String(v))}
            className={`py-3 rounded-xl text-sm font-semibold transition ${
              String(v) === amount ? "btn-gold" : "btn-outline-gold"
            }`}
            data-testid={`quick-amount-${v}`}
          >
            ₹{v}
          </button>
        ))}
      </div>

      <div className="card-glass rounded-2xl p-5 fade-slide-up">
        <div className="stat-label mb-2">Or enter custom amount</div>
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold text-[#E6B455]">₹</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-4xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
            data-testid="topup-amount-input"
          />
        </div>
      </div>

      <div className="mt-6">
        {done ? (
          <div className="btn-gold rounded-xl p-4 flex items-center justify-center gap-2" data-testid="topup-done">
            <Check size={18} /> Wallet topped up!
          </div>
        ) : (
          <button
            onClick={topUp}
            disabled={!Number(amount) || busy}
            className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="topup-pay-btn"
          >
            <Plus size={18} /> {busy ? "Opening Razorpay…" : `Add ₹${amount || 0} to wallet`}
          </button>
        )}
      </div>
    </div>
  );
}
